// ============================================================================
// Sign-out plumbing shared by the idle detector and the login banner
// (D-52, 19H §0.8). Pure browser helpers; no supabase import, so it is testable
// in isolation and cannot create an import cycle.
//
// TWO KINDS OF SIGN-OUT reach a tab's SIGNED_OUT listener:
//   * a DELIBERATE one — the Sign out button, the idle detector — which already
//     navigates to /login itself;
//   * one that HAPPENED to the tab — the 24 h timebox, a revoke from the Sessions
//     card on another device, another tab signing out — where the refresh simply
//     failed and supabase-js cleared the session. The listener must send that tab
//     to /login with a reason, because nothing else will.
// `markIntentionalSignOut()` is how the first kind says so. And because a
// SIGNED_OUT in tab B caused by tab A idling out would otherwise read as a
// generic "expired", the reason is also left in localStorage for a short window
// so the other tabs show the same banner.
//
// BYTE-FOR-BYTE THE SAME FILE in admin-dashboard/ and customer-portal/ (and the
// same rules as TPRM7's apps/admin/src/lib/session-signout.ts); see
// backend/tests/unit/session-standard.parity.test.ts.
// ============================================================================

export const INACTIVITY_REASON = 'inactivity';
export const EXPIRED_REASON = 'expired';
export type LoginReason = typeof INACTIVITY_REASON | typeof EXPIRED_REASON;

const REASON_STORAGE_KEY = 'vw:signout-reason';
/** How long another tab may still attribute its SIGNED_OUT to the remembered reason. */
export const SIGNOUT_REASON_MAX_AGE_MS = 30_000;

const KNOWN_REASONS: readonly string[] = [INACTIVITY_REASON, EXPIRED_REASON];

function isLoginReason(v: unknown): v is LoginReason {
  return typeof v === 'string' && KNOWN_REASONS.includes(v);
}

export function loginUrlForReason(reason: LoginReason | null): string {
  return reason ? `/login?reason=${reason}` : '/login';
}

/** The reason a /login query string carries, only if it is one we issue. */
export function readLoginReason(search: string): LoginReason | null {
  const v = new URLSearchParams(search).get('reason');
  return isLoginReason(v) ? v : null;
}

/**
 * The locked banner copy (19H §0.8 and the timebox treatment in §1.7). Anything
 * that is not exactly one of our two reasons renders nothing: the query string is
 * attacker-controllable, and a banner must never print text from it.
 */
export function loginReasonMessage(reason: string | null): string | null {
  if (reason === INACTIVITY_REASON) return 'You were signed out due to inactivity.';
  if (reason === EXPIRED_REASON) return 'Please sign in again.';
  return null;
}

let intentionalSignOut = false;

/** Call immediately before a sign-out this tab starts itself. */
export function markIntentionalSignOut(): void {
  intentionalSignOut = true;
}

/** True exactly once after markIntentionalSignOut(); the SIGNED_OUT listener calls it. */
export function consumeIntentionalSignOut(): boolean {
  const was = intentionalSignOut;
  intentionalSignOut = false;
  return was;
}

/** Leave the reason where the OTHER tabs' SIGNED_OUT listeners can read it. */
export function rememberSignOutReason(reason: LoginReason): void {
  try {
    window.localStorage.setItem(REASON_STORAGE_KEY, JSON.stringify({ reason, at: Date.now() }));
  } catch {
    // best-effort: other tabs fall back to the generic "Please sign in again".
  }
}

/** A reason remembered within SIGNOUT_REASON_MAX_AGE_MS, else null. */
export function recentSignOutReason(): LoginReason | null {
  try {
    const raw = window.localStorage.getItem(REASON_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { reason?: unknown; at?: unknown };
    if (!isLoginReason(parsed.reason) || typeof parsed.at !== 'number') return null;
    if (Date.now() - parsed.at > SIGNOUT_REASON_MAX_AGE_MS) return null;
    return parsed.reason;
  } catch {
    return null; // unreadable or malformed: treat as "no remembered reason"
  }
}

/** Hard navigation, so no in-memory module or React state survives into the next session. */
export function redirectToLogin(reason: LoginReason | null): void {
  window.location.assign(loginUrlForReason(reason));
}
